<script lang="ts">
export default {
  name: 'CoverageTypeEditModal',
};
</script>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { Pencil } from '@lucide/vue';
import { Product } from '@/types/Product';
import { policyTypeService } from '@/services/PolicyTypeService';

const props = defineProps<{
  coverageType: string;
  coverageTypes: string[];
  products: Product[];
  coverageTypeId: number;
  productName?: string;
}>();

const emit = defineEmits<{
  updated: [];
}>();

// Reactive state
const newCoverageTypeName = ref(props.coverageType);
const error = ref('');
const productError = ref('');
const showModal = ref(false);
const loading = ref(false);
const selectedProductId = ref<number | undefined>(undefined);
const originalProductId = ref<number | undefined>(undefined);

// Computed properties
const productOptions = computed(() => {
  return props.products.map(product => ({
    value: product.id,
    label: product.name
  }));
});

// Watch for prop changes to update the input value
watch(
  () => props.coverageType,
  (newValue) => {
    if (!showModal.value) {
      newCoverageTypeName.value = newValue;
    }
  }
);

// Methods
const openModal = () => {
  newCoverageTypeName.value = props.coverageType;
  error.value = '';
  productError.value = '';
  
  // Look up current product ID
  const product = props.products.find(p => p.name === props.productName);
  selectedProductId.value = product?.id;
  originalProductId.value = product?.id;
  
  showModal.value = true;
};

const handleSave = async () => {
  // Reset errors
  error.value = '';
  productError.value = '';
  
  // Check if name or product changed
  const nameChanged = newCoverageTypeName.value.trim() !== props.coverageType;
  const productChanged = selectedProductId.value !== originalProductId.value;
  
  // Validate name if it changed
  if (nameChanged) {
    if (newCoverageTypeName.value.trim() === '') {
      error.value = 'Coverage type name cannot be empty';
      return;
    }
    if (props.coverageTypes.includes(newCoverageTypeName.value.trim())) {
      error.value = 'Cannot rename Coverage type to an existing coverage type';
      return;
    }
  }
  
  // If nothing changed, show error
  if (!nameChanged && !productChanged) {
    error.value = 'No changes to save';
    return;
  }
  
  try {
    loading.value = true;
    let nameSuccess = true;
    let productSuccess = true;
    
    // Attempt to rename if name changed
    if (nameChanged) {
      try {
        await policyTypeService.RenameCoverageType({
          oldName: props.coverageType,
          newName: newCoverageTypeName.value.trim()
        });
      } catch (e) {
        console.error('Failed to rename coverage type:', e);
        error.value = 'Failed to rename coverage type';
        nameSuccess = false;
      }
    }
    
    // Attempt to update product if product changed
    if (productChanged && selectedProductId.value !== undefined) {
      try {
        await policyTypeService.UpdateCoverageTypeProduct(props.coverageTypeId, selectedProductId.value);
      } catch (e) {
        console.error('Failed to update product mapping:', e);
        productError.value = 'Failed to update product mapping. Please try again.';
        productSuccess = false;
      }
    }
    
    // Close modal and trigger refresh only if all operations succeeded
    if (nameSuccess && productSuccess) {
      emit('updated');
      showModal.value = false;
    }
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  newCoverageTypeName.value = props.coverageType;
  error.value = '';
  productError.value = '';
  selectedProductId.value = undefined;
  originalProductId.value = undefined;
  showModal.value = false;
};
</script>

<template>
  <div>
    <button
      class="btn btn-ghost btn-sm opacity-30 transition-opacity group-hover:opacity-100"
      @click="openModal"
      :data-test="`${coverageType}-edit`">
      <Pencil :size="16" />
    </button>

    <AppModal
      :show="showModal"
      :title="`Edit Coverage Type: ${coverageType}`"
      :loading="loading"
      actionButtonLabel="Save"
      :handleAction="handleSave"
      :handleCancel="handleCancel"
      size="md">
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="w-full">
            <label class="label">
              <span class="label-text text-xs text-gray-500">Coverage Type Name</span>
            </label>
            <input
              type="text"
              v-model="newCoverageTypeName"
              class="input input-md w-full border border-solid"
              :class="{ 'border-red-500': error }"
              :data-test="`${coverageType}-name-input`"
              placeholder="Enter coverage type name..." />
            <label v-if="error" class="label">
              <span class="label-text-alt text-xs text-red-500">{{ error }}</span>
            </label>
          </div>
          
          <div class="w-full">
            <label class="label">
              <span class="label-text text-xs text-gray-500">Product Mapping</span>
            </label>
            <AppSelect
              v-model="selectedProductId"
              :options="productOptions"
              :error="!!productError"
              placeholder="Select a product..."
              :data-test="`${coverageType}-product-select`" />
            <label v-if="productError" class="label">
              <span class="label-text-alt text-xs text-red-500">{{ productError }}</span>
            </label>
          </div>
        </div>
      </template>
    </AppModal>
  </div>
</template>
